import React, { useState, useEffect } from "react";
import { UiModal } from "../../../../Components/Shared/UiModal";

const EMPTY_FORM = {
  name: "",
  multiplier: "",
  description: "",
  isFarFlung: false,
};

export default function OutStationZoneModal({
  isOpen,
  onClose,
  onSubmit,
  editZone,
}) {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!isOpen) {
      setFormData(EMPTY_FORM);
      setErrors({});
      return;
    }

    if (editZone) {
      setFormData({
        name: editZone.zone_name || "",
        multiplier: editZone.multiplier || "",
        description: editZone.region_description || "",
        isFarFlung: editZone.isFarFlung || false,
      });
    } else {
      setFormData(EMPTY_FORM);
    }
    setErrors({});
  }, [isOpen, editZone]);

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;

    if (name === "multiplier") {
      const regex = /^\d*\.?\d*$/;
      if (regex.test(value) || value === "") {
        setFormData((prev) => ({ ...prev, [name]: value }));
      }
    } else if (type === "radio") {
      setFormData((prev) => ({ ...prev, [name]: value === "true" }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Zone name is required";
    } else if (formData.name.trim().length < 2) {
      newErrors.name = "Zone name must be at least 2 characters long";
    }

    if (!formData.multiplier) {
      newErrors.multiplier = "Multiplier is required";
    } else if (
      isNaN(parseFloat(formData.multiplier)) ||
      parseFloat(formData.multiplier) <= 0
    ) {
      newErrors.multiplier = "Multiplier must be a positive number";
    }

    if (!formData.description.trim()) {
      newErrors.description = "Description is required";
    } else if (formData.description.trim().length < 5) {
      newErrors.description = "Description must be at least 5 characters long";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (validateForm()) {
      const submitData = {
        zone_name: formData.name.trim(),
        region_description: formData.description.trim(),
        multiplier: parseFloat(formData.multiplier),
        isFarFlung: formData.isFarFlung,
      };

      onSubmit(submitData, editZone?.id);
      setFormData(EMPTY_FORM);
      setErrors({});
    }
  };

  const isFormValid =
    formData.name.trim().length >= 2 &&
    formData.multiplier &&
    !isNaN(parseFloat(formData.multiplier)) &&
    parseFloat(formData.multiplier) > 0 &&
    formData.description.trim().length >= 5;

  const isEditMode = Boolean(editZone);
  const fieldClass = (name) =>
    `ui-input-field ${errors[name] ? "border-brand-error" : ""}`;

  return (
    <UiModal
      isOpen={isOpen}
      onClose={onClose}
      eyebrow="Delivery Management"
      title={isEditMode ? "Edit Out Station Zone" : "Add Out Station Zone"}
      as="form"
      onSubmit={handleSubmit}
      footer={
        <>
          <button
            type="button"
            className="ui-btn-cancel min-w-[108px]"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!isFormValid}
            className="ui-btn-save min-w-[108px]"
          >
            {isEditMode ? "Update" : "Save"}
          </button>
        </>
      }
    >
      <section className="ui-modal-section">
        <h4 className="ui-modal-section-title">Zone details</h4>
        <div className="space-y-4">
          <div>
            <label className="ui-form-label">
              Out Station Zone Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className={fieldClass("name")}
              placeholder="Enter out station zone name"
            />
            {errors.name ? (
              <p className="ui-form-error">{errors.name}</p>
            ) : null}
          </div>

          <div>
            <label className="ui-form-label">
              Out Station Multiplier <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="multiplier"
              value={formData.multiplier}
              onChange={handleInputChange}
              className={fieldClass("multiplier")}
              placeholder="e.g., 1.2, 1.6, 3, 4"
            />
            {errors.multiplier ? (
              <p className="ui-form-error">{errors.multiplier}</p>
            ) : null}
          </div>

          <div>
            <label className="ui-form-label">
              Out Station Description <span className="text-red-500">*</span>
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={4}
              className={`ui-textarea-field ${
                errors.description ? "border-brand-error" : ""
              }`}
              placeholder="Enter out station zone description"
            />
            {errors.description ? (
              <p className="ui-form-error">{errors.description}</p>
            ) : null}
          </div>

          <div>
            <label className="ui-form-label">Far Flung</label>
            <div className="flex space-x-6">
              <label className="flex cursor-pointer items-center">
                <input
                  type="radio"
                  name="isFarFlung"
                  value="true"
                  checked={formData.isFarFlung === true}
                  onChange={handleInputChange}
                  className="form-radio mr-2 cursor-pointer text-blue-600"
                />
                <span>Yes</span>
              </label>
              <label className="flex cursor-pointer items-center">
                <input
                  type="radio"
                  name="isFarFlung"
                  value="false"
                  checked={formData.isFarFlung === false}
                  onChange={handleInputChange}
                  className="form-radio mr-2 cursor-pointer text-blue-600"
                />
                <span>No</span>
              </label>
            </div>
          </div>
        </div>
      </section>
    </UiModal>
  );
}
